import { useEffect, useState } from "react";
import { api, API_BASE, formatMoney, formatDate, todayISO, openDownload } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, FileDown } from "lucide-react";
import ImportButton from "@/components/ImportButton";

export default function TxPage({ type }) {
  const isIncome = type === "income";
  const [items, setItems] = useState([]);
  const [banks, setBanks] = useState([]);
  const [cari, setCari] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    type, amount: "", currency: "TRY", category: "", description: "",
    date: todayISO(), bank_account_id: "", cari_id: "",
  });

  const load = async () => {
    const [t, b, c] = await Promise.all([
      api.get(`/transactions?type=${type}`),
      api.get("/bank-accounts"),
      api.get("/cari"),
    ]);
    setItems(t.data); setBanks(b.data); setCari(c.data);
  };
  useEffect(() => { load(); }, [type]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/transactions", { ...form, amount: Number(form.amount) });
      toast.success(isIncome ? "Gelir eklendi" : "Gider eklendi");
      setOpen(false);
      setForm({ ...form, amount: "", category: "", description: "" });
      load();
    } catch {
      toast.error("Kayıt başarısız");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`/transactions/${id}`);
    toast.success("Silindi");
    load();
  };

  const total = items.reduce((s, i) => s + i.amount, 0);

  const exportXlsx = () => {
    const token = localStorage.getItem("madymark_token");
    openDownload(`/api/export/transactions.xlsx`, { type });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">{isIncome ? "Gelir Yönetimi" : "Gider Yönetimi"}</div>
          <h1 className="font-display text-4xl font-extrabold text-slate-900 mt-1">{isIncome ? "Gelirler" : "Giderler"}</h1>
          <p className="text-slate-500 mt-1">Toplam {items.length} kayıt · <span className={`font-mono-num font-bold ${isIncome ? "text-emerald-600" : "text-rose-600"}`}>{formatMoney(total, "TRY")}</span></p>
        </div>
        <div className="flex gap-2">
          <ImportButton kind="transactions" label="Excel İçe Aktar" onDone={load} />
          <button data-testid={`export-${type}`} onClick={exportXlsx} className="flex items-center gap-2 px-4 py-3 rounded-xl bg-white border border-slate-200 hover:border-emerald-500 text-slate-700 hover:text-emerald-600 font-bold transition">
            <FileDown className="w-4 h-4" /> Excel
          </button>
          <button data-testid={`add-${type}`} onClick={() => setOpen(true)} className="flex items-center gap-2 px-5 py-3 rounded-xl bg-[#1E2A9C] text-white font-bold hover:bg-[#121A68] transition shadow-lg shadow-[#1E2A9C]/20">
            <Plus className="w-4 h-4" /> Yeni Kayıt
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-widest">
            <tr>
              <th className="text-left px-6 py-3 font-bold">Tarih</th>
              <th className="text-left px-6 py-3 font-bold">Kategori</th>
              <th className="text-left px-6 py-3 font-bold">Açıklama</th>
              <th className="text-left px-6 py-3 font-bold">Cari</th>
              <th className="text-right px-6 py-3 font-bold">Tutar</th>
              <th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="text-center py-16 text-slate-400">Henüz kayıt yok</td></tr>}
            {items.map((t) => (
              <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                <td className="px-6 py-4 font-mono-num text-slate-700">{formatDate(t.date)}</td>
                <td className="px-6 py-4"><span className="px-2.5 py-1 bg-slate-100 rounded-md text-xs font-semibold">{t.category}</span></td>
                <td className="px-6 py-4 text-slate-800">{t.description}</td>
                <td className="px-6 py-4 text-slate-600">{cari.find((c) => c.id === t.cari_id)?.name || "-"}</td>
                <td className={`px-6 py-4 text-right font-mono-num font-bold ${isIncome ? "text-emerald-600" : "text-rose-600"}`}>
                  {isIncome ? "+" : "−"}{formatMoney(t.amount, t.currency)}
                </td>
                <td className="px-6 py-4 text-right"><button data-testid={`del-tx-${t.id}`} onClick={() => del(t.id)} className="text-slate-400 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 grid place-items-center p-4">
          <form onSubmit={submit} className="bg-white rounded-2xl w-full max-w-lg p-8 relative">
            <button type="button" onClick={() => setOpen(false)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
            <div className="font-display text-2xl font-extrabold mb-6">{isIncome ? "Yeni Gelir" : "Yeni Gider"}</div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Tutar</label>
                <input required type="number" step="0.01" data-testid="tx-amount" value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg font-mono-num" /></div>
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Para Birimi</label>
                <select data-testid="tx-currency" value={form.currency} onChange={(e) => setForm({...form, currency: e.target.value})} className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg">
                  {["TRY","USD","EUR"].map(c => <option key={c} value={c}>{c}</option>)}
                </select></div>
              <div className="col-span-2"><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Kategori</label>
                <input required data-testid="tx-category" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} placeholder={isIncome ? "Satış, Hizmet Bedeli..." : "Kira, Maaş, Vergi..."} className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg" /></div>
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Tarih</label>
                <input type="date" data-testid="tx-date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg" /></div>
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Banka</label>
                <select data-testid="tx-bank" value={form.bank_account_id} onChange={(e) => setForm({...form, bank_account_id: e.target.value})} className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg">
                  <option value="">-</option>
                  {banks.filter(b => b.currency === form.currency).map(b => <option key={b.id} value={b.id}>{b.bank_name} ({b.name})</option>)}
                </select></div>
              <div className="col-span-2"><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Cari (opsiyonel)</label>
                <select data-testid="tx-cari" value={form.cari_id} onChange={(e) => setForm({...form, cari_id: e.target.value})} className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg">
                  <option value="">-</option>
                  {cari.map(c => <option key={c.id} value={c.id}>{c.name} ({c.type === "customer" ? "Müşteri" : "Tedarikçi"})</option>)}
                </select></div>
              <div className="col-span-2"><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Açıklama</label>
                <textarea data-testid="tx-desc" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows="2" className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg"></textarea></div>
            </div>
            <button data-testid="tx-submit" className="w-full mt-6 py-3 bg-[#1E2A9C] text-white rounded-lg font-bold">Kaydet</button>
          </form>
        </div>
      )}
    </div>
  );
}
